import { css } from 'aphrodite';
import { useAIConversationChatMessageQuery } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../web/components-v2/ai/ChatConversation';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { styleSheet } from './styles';
import { TranscriptMessage } from './TranscriptMessage';

interface IProps {
	conversationId: string | null;
}

/**
 * Right-hand detail panel for the Coffee AI coaching review page. Given the currently selected
 * conversation id, it fetches that conversation's chat transcript via
 * `useAIConversationChatMessageQuery` (the non-suspense variant, since this page is not wrapped
 * in Suspense) and renders each turn with the local `TranscriptMessage` row inside the shared
 * `ChatConversation` / `ChatConversationContent` scroll container, so the transcript scrolls
 * identically to the reference page.
 *
 * We deliberately do NOT reuse the customer-facing `AIConversationMessage` here: it only renders an
 * assistant turn's rich `contents` and ignores the flat `text` field and the `System` role
 * entirely, which blanks SalesLiveCoaching coaching transcripts (their turns are plain text and may
 * be `System` messages). `TranscriptMessage` renders text robustly for every role.
 *
 * States: a placeholder when nothing is selected, the shared
 * `LoadingSpinner` while fetching, an empty state when the conversation has no messages, and an
 * empty body on query error (the error toast is surfaced by the hook, so we avoid rendering a
 * broken thread). Assistant rows include thumbs-up/down feedback controls.
 */
export function TranscriptPanel({ conversationId }: IProps) {
	const {
		data: messages,
		isLoading,
		isError,
	} = useAIConversationChatMessageQuery({
		id: conversationId ?? '',
		enabled: Boolean(conversationId),
	});

	if (!conversationId) {
		return <div className={css(styleSheet.panelBody)}>Select a conversation to view its transcript</div>;
	}

	if (isLoading) {
		return (
			<div className={css(styleSheet.detailBody)}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	// Errors are surfaced by the query's `meta.toastErrorMessage`; avoid rendering a broken thread.
	if (isError || !messages || messages.length === 0) {
		return <div className={css(styleSheet.panelBody)}>No messages in this conversation</div>;
	}

	return (
		<div className={css(styleSheet.detailBody)}>
			<ChatConversation styles={[styleSheet.thread]}>
				<ChatConversationContent styles={[styleSheet.threadContent]}>
					{messages.map((message, index) => (
						<TranscriptMessage
							key={`${conversationId}-${message.id ?? index}`}
							conversationId={conversationId}
							message={message}
						/>
					))}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
		</div>
	);
}
